import {useEffect, useMemo, useRef, useState} from "react";
import toast from "react-hot-toast";
import {useTranslation} from "react-i18next";
import {AdministrationApi} from "../../api/authority/generated/index.ts";
import useAuthorityConfiguration from "../../api/utilities/useAuthorityConfiguration.js";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import Badge from "../../components/Badge.jsx";
import LoadingComponent from "../../components/LoadingComponent.jsx";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import {TablePagination} from "./ReportingLab.jsx";

const actions = [
    "trial_started", "subscription_requested", "subscription_activated", "subscription_changed",
    "subscription_change_scheduled", "cancellation_scheduled", "cancellation_cleared",
    "subscription_canceled", "renewal_succeeded", "renewal_failed",
];

function actionLabel(action, t) {
    const labels = {
        trial_started: t("admin.subscription_audit.action_trial_started", {defaultValue: "Zahájení zkoušky"}),
        subscription_requested: t("admin.subscription_audit.action_subscription_requested", {defaultValue: "Výběr placeného balíčku"}),
        subscription_activated: t("admin.subscription_audit.action_subscription_activated", {defaultValue: "Aktivace předplatného"}),
        subscription_changed: t("admin.subscription_audit.action_subscription_changed", {defaultValue: "Změna balíčku"}),
        subscription_change_scheduled: t("admin.subscription_audit.action_subscription_change_scheduled", {defaultValue: "Naplánovaná změna balíčku"}),
        cancellation_scheduled: t("admin.subscription_audit.action_cancellation_scheduled", {defaultValue: "Naplánované zrušení"}),
        cancellation_cleared: t("admin.subscription_audit.action_cancellation_cleared", {defaultValue: "Odvolané zrušení"}),
        subscription_canceled: t("admin.subscription_audit.action_subscription_canceled", {defaultValue: "Ukončení předplatného"}),
        renewal_succeeded: t("admin.subscription_audit.action_renewal_succeeded", {defaultValue: "Úspěšné obnovení"}),
        renewal_failed: t("admin.subscription_audit.action_renewal_failed", {defaultValue: "Neúspěšné obnovení"}),
    };
    return labels[action] ?? action;
}

function formatDate(value) {
    return value ? new Intl.DateTimeFormat("cs-CZ", {dateStyle: "medium", timeStyle: "medium", timeZone: "Europe/Prague"}).format(new Date(value)) : "—";
}

function transition(previous, next) {
    if (!previous && !next) return "—";
    if (!previous || previous === next) return next ?? previous;
    return `${previous} → ${next ?? "—"}`;
}

export default function SubscriptionAudit() {
    const {t} = useTranslation();
    const configuration = useAuthorityConfiguration();
    const {et: translateError} = useErrorTranslation();
    const requestDependenciesRef = useRef({configuration, translateError, t});
    requestDependenciesRef.current = {configuration, translateError, t};
    const [data, setData] = useState({items: [], total: 0, sources: []});
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [action, setAction] = useState("");
    const [actorType, setActorType] = useState("");
    const [source, setSource] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    useEffect(() => {
        const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => window.clearTimeout(timeout);
    }, [search]);

    useEffect(() => setPage(1), [debouncedSearch, action, actorType, source, pageSize]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const {configuration: currentConfiguration} = requestDependenciesRef.current;
        new AdministrationApi(currentConfiguration).listAdminSubscriptionAudit({
            page, pageSize,
            search: debouncedSearch || undefined,
            action: action || undefined,
            actorType: actorType || undefined,
            source: source || undefined,
        }).then((result) => {
            if (!cancelled) setData({items: result.items ?? [], total: result.total ?? 0, sources: result.sources ?? []});
        }).catch(async (error) => {
            const {translateError: translateCurrentError, t: translate} = requestDependenciesRef.current;
            if (!cancelled) toast.error(await translateCurrentError(error) || translate("admin.subscription_audit.load_failed", {defaultValue: "Audit předplatného se nepodařilo načíst."}));
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [page, pageSize, debouncedSearch, action, actorType, source]);

    const sources = useMemo(() => Array.from(new Set([...(data.sources ?? []), source].filter(Boolean))).sort(), [data.sources, source]);

    return <WideSecureLayout title={t("admin.subscription_audit.title", {defaultValue: "Audit předplatného"})}>
        <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">{t("admin.subscription_audit.description", {defaultValue: "Důkazní historie změn balíčků a předplatného. Záznamy jsou pouze pro čtení a nelze je zpětně upravit."})}</p>
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
                <div className="grid gap-3 border-b border-slate-200 p-4 md:grid-cols-2 xl:grid-cols-4 dark:border-slate-700">
                    <input className="form-input w-full" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("admin.subscription_audit.search", {defaultValue: "Firma, projekt, uživatel nebo e-mail…"})}/>
                    <select className="form-select w-full" value={action} onChange={(event) => setAction(event.target.value)}>
                        <option value="">{t("admin.subscription_audit.all_actions", {defaultValue: "Všechny změny"})}</option>
                        {actions.map((value) => <option key={value} value={value}>{actionLabel(value, t)}</option>)}
                    </select>
                    <select className="form-select w-full" value={actorType} onChange={(event) => setActorType(event.target.value)}>
                        <option value="">{t("admin.subscription_audit.all_actors", {defaultValue: "Všichni původci"})}</option>
                        <option value="user">{t("admin.subscription_audit.actor_user", {defaultValue: "Uživatel"})}</option>
                        <option value="system">{t("admin.subscription_audit.actor_system", {defaultValue: "Systém"})}</option>
                    </select>
                    <select className="form-select w-full" value={source} onChange={(event) => setSource(event.target.value)}>
                        <option value="">{t("admin.subscription_audit.all_sources", {defaultValue: "Všechny zdroje"})}</option>
                        {sources.map((value) => <option key={value} value={value}>{value}</option>)}
                    </select>
                </div>
                <div className="relative overflow-x-auto">
                    {loading && <LoadingComponent/>}
                    <table className="min-w-[1100px] w-full text-sm">
                        <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-900/50 dark:text-slate-400"><tr>
                            <th className="px-4 py-3">{t("admin.subscription_audit.time", {defaultValue: "Čas"})}</th>
                            <th className="px-4 py-3">{t("admin.subscription_audit.project", {defaultValue: "Firma / projekt"})}</th>
                            <th className="px-4 py-3">{t("admin.subscription_audit.action", {defaultValue: "Změna"})}</th>
                            <th className="px-4 py-3">{t("admin.subscription_audit.actor", {defaultValue: "Provedl"})}</th>
                            <th className="px-4 py-3">{t("admin.subscription_audit.package", {defaultValue: "Balíček"})}</th>
                            <th className="px-4 py-3">{t("admin.subscription_audit.state", {defaultValue: "Stav / zrušení"})}</th>
                        </tr></thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {(data.items ?? []).map((row) => <tr key={row.id} className="hover:bg-violet-50/50 dark:hover:bg-violet-950/20">
                            <td className="whitespace-nowrap px-4 py-4">{formatDate(row.occurredAt)}</td>
                            <td className="px-4 py-4">
                                <div className="font-semibold text-slate-900 dark:text-white">{row.companyName || row.projectName}</div>
                                {row.companyName && <div className="text-xs text-slate-500 dark:text-slate-400">{row.projectName}</div>}
                                <div className="font-mono text-xs text-slate-400">{row.projectId}</div>
                            </td>
                            <td className="px-4 py-4"><Badge color={row.action === "renewal_failed" ? "rose" : "violet"}>{actionLabel(row.action, t)}</Badge><div className="mt-1 text-xs text-slate-400">{row.source}</div></td>
                            <td className="px-4 py-4">{row.actorType === "system" ? t("admin.subscription_audit.actor_system", {defaultValue: "Systém"}) : (row.actorName || row.actorEmail || "—")}{row.actorName && row.actorEmail && <div className="text-xs text-slate-400">{row.actorEmail}</div>}</td>
                            <td className="px-4 py-4"><div>{transition(row.previousPackageName, row.newPackageName)}</div><div className="text-xs text-slate-400">{transition(row.previousBillingCycle, row.newBillingCycle)}</div></td>
                            <td className="px-4 py-4"><div>{transition(row.previousStatus, row.newStatus)}</div>{row.newCancelAtPeriodEnd !== row.previousCancelAtPeriodEnd && <div className="mt-1 text-xs text-slate-500">{row.newCancelAtPeriodEnd ? t("admin.subscription_audit.cancellation_yes", {defaultValue: "Zrušení naplánováno"}) : t("admin.subscription_audit.cancellation_no", {defaultValue: "Zrušení odvoláno"})}</div>}</td>
                        </tr>)}
                        {!loading && (data.items ?? []).length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">{t("admin.subscription_audit.empty", {defaultValue: "Filtrům neodpovídá žádná změna."})}</td></tr>}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 pb-4"><TablePagination page={page} pageSize={pageSize} totalItems={data.total ?? 0} onPageChange={setPage} onPageSizeChange={setPageSize} t={t}/></div>
            </section>
        </div>
    </WideSecureLayout>;
}
